import { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { getPoshCommitteeMembers, createPoshCommittee, getPoshCommittees } from '../../../api/IncidentAPI'

interface PoshCommitteeFormProps {
  onTabChange: (tab: string) => void
}

const PoshCommitteeForm: React.FC<PoshCommitteeFormProps> = ({ onTabChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    memberIds: [] as string[],
  })

  const [availableMembers, setAvailableMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchingMembers, setFetchingMembers] = useState(false)

  useEffect(() => {
    fetchPoshCommitteeMembers()
  }, [])

  const fetchPoshCommitteeMembers = async () => {
    setFetchingMembers(true)
    try {
      const response = await getPoshCommitteeMembers()
      if (response.statusCode === 200) {
        setAvailableMembers(response.data || [])
      } else {
        toast.error(response.message || 'Failed to fetch POSH committee members')
      }
    } catch (error) {
      console.error('Error fetching POSH committee members:', error)
      toast.error('Failed to fetch POSH committee members')
    } finally {
      setFetchingMembers(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleMemberToggle = (memberId: string) => {
    setFormData(prev => ({
      ...prev,
      memberIds: prev.memberIds.includes(memberId)
        ? prev.memberIds.filter(id => id !== memberId)
        : [...prev.memberIds, memberId],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Committee name is required')
      return
    }

    if (formData.memberIds.length === 0) {
      toast.error('At least one committee member is required')
      return
    }

    setLoading(true)
    try {
      const normalizedName = formData.name.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
      const existingResponse = await getPoshCommittees({ page: 1, limit: 1000 })
      const existingCommittees = existingResponse.statusCode === 200 ? existingResponse.data?.committees || [] : []
      if (existingCommittees.some((committee: any) => committee.name.trim().replace(/\s+/g, ' ').toLocaleLowerCase() === normalizedName)) {
        toast.error('A committee with this name already exists')
        return
      }
      const response = await createPoshCommittee({ ...formData, name: formData.name.trim().replace(/\s+/g, ' ') })
      if (response.statusCode === 201) {
        toast.success('POSH Committee created successfully')
        onTabChange('list')
      } else {
        toast.error(response.message || 'Failed to create POSH Committee')
      }
    } catch (error) {
      console.error('Error creating POSH Committee:', error)
      toast.error('Failed to create POSH Committee')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onTabChange('list')
  }

  return (
    <div className="posh-committee-form">
      <div className="card">
        <div className="card-header">
          <h3>Create POSH Committee</h3>
        </div>
        <div className="card-body">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="required fw-bold">Committee Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter committee name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-bold">Description (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter committee description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="required fw-bold">
                    Committee Members
                    {fetchingMembers && <span className="ms-2 text-muted">(Loading...)</span>}
                  </Form.Label>
                  {availableMembers.length === 0 && !fetchingMembers ? (
                    <div className="alert alert-info">
                      No active POSH committee members found. Please mark employees as POSH Committee Members in their user profile first.
                    </div>
                  ) : (
                    <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {availableMembers.map(member => (
                        <div key={member.id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`member-${member.id}`}
                            checked={formData.memberIds.includes(member.id)}
                            onChange={() => handleMemberToggle(member.id)}
                          />
                          <label className="form-check-label" htmlFor={`member-${member.id}`}>
                            <strong>{member.firstName} {member.lastName}</strong>
                            {member.employeeCode && <span className="text-muted ms-2">({member.employeeCode})</span>}
                            {member.email && <span className="text-muted ms-2">- {member.email}</span>}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Select at least one member. Only active employees marked as POSH Committee Members are shown.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={loading || fetchingMembers}
              >
                {loading ? 'Creating...' : 'Create Committee'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default PoshCommitteeForm
